<script setup lang="ts">
/** MultiSelectDropdown */
/* A dropdown of fixed options where any number can be selected. The trigger
   lists the selected labels; the menu stays open between picks.
   Example: the grade and subject-domain pickers on the admin anchor-page editor. */
import { computed } from 'vue'
import {
  SelectContent,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectViewport
} from 'reka-ui'
import { menuItemFocusRing } from '@/components/dropdown-menu/focus-ring'

export interface MultiSelectOption {
  label: string
  value: string
}

const props = withDefaults(defineProps<{
  id: string
  options: MultiSelectOption[]
  modelValue: string[]
  placeholder?: string
  disabled?: boolean
}>(), {
  placeholder: 'Select...',
  disabled: false
})

const emit = defineEmits<{ 'update:modelValue': [value: string[]] }>()

const selectedSummary = computed(() =>
  props.modelValue
    .map(value => props.options.find(option => option.value === value)?.label ?? value)
    .join(', ')
)

function emitSelection(value: string | string[]) {
  emit('update:modelValue', Array.isArray(value) ? value : [value])
}
</script>

<template>
  <SelectRoot
    :model-value="modelValue"
    :disabled="disabled"
    multiple
    @update:model-value="emitSelection"
  >
    <SelectTrigger
      :id="id"
      class="w-full h-10 px-3 flex items-center justify-between gap-2 text-left text-md text-gray-900 bg-white border border-gray-600 rounded-xs focus-visible:outline-primary-400 data-[disabled]:bg-gray-300 data-[disabled]:text-gray-700 data-[disabled]:cursor-not-allowed"
    >
      <span v-if="selectedSummary" class="truncate">{{ selectedSummary }}</span>
      <span v-else class="truncate text-gray-700">{{ placeholder }}</span>
      <span class="material-icons-round text-display-sm text-gray-900" aria-hidden="true">arrow_drop_down</span>
    </SelectTrigger>
    <SelectPortal>
      <SelectContent
        position="popper"
        :side-offset="4"
        aria-multiselectable="true"
        class="w-[--reka-select-trigger-width] bg-white border border-gray-600 rounded-xs shadow-md z-50"
      >
        <SelectViewport class="py-1 max-h-[--reka-select-content-available-height] overflow-y-auto">
          <SelectItem
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            class="flex items-center justify-between gap-2 px-4 py-2 text-md text-gray-900 cursor-pointer select-none outline-none data-[highlighted]:bg-gray-300 data-[state=checked]:font-bold"
            :class="menuItemFocusRing"
          >
            <SelectItemText>{{ option.label }}</SelectItemText>
            <SelectItemIndicator>
              <span class="material-icons-round text-md" aria-hidden="true">check</span>
            </SelectItemIndicator>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
